// src/components/store/sections/navbar/VisualSearchModal.tsx
"use client";

import { useState, useRef, useEffect, useCallback } from "react";
import { useRouter } from "next/navigation";
import {
  Camera,
  Upload,
  X,
  Search,
  Sparkles,
  Loader2,
  RefreshCw,
  ShoppingBag,
  Check,
  Image as ImageIcon,
  SwitchCamera,
  AlertCircle,
} from "lucide-react";

interface VisualSearchModalProps {
  isOpen?: boolean;
  onClose?: () => void;
  subdomain?: string;
  primaryColor?: string;
  branchId?: number | string | null;
}

export default function VisualSearchModal({
  isOpen,
  onClose,
  subdomain = "nextlook",
  primaryColor = "#059669",
  branchId = null,
}: VisualSearchModalProps) {
  const router = useRouter();
  const [internalOpen, setInternalOpen] = useState(false);
  const effectiveOpen = isOpen !== undefined ? isOpen : internalOpen;

  const handleCloseModal = useCallback(() => {
    setInternalOpen(false);
    if (onClose) onClose();
  }, [onClose]);

  const [activeTab, setActiveTab] = useState<"upload" | "camera">("upload");
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [results, setResults] = useState<any[]>([]);
  const [detectedQuery, setDetectedQuery] = useState<string>("");
  const [detectedTags, setDetectedTags] = useState<string[]>([]);
  const [addedIds, setAddedIds] = useState<(number | string)[]>([]);

  // Live Camera Refs & States
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [cameraFacing, setCameraFacing] = useState<"environment" | "user">("environment");
  const [cameraActive, setCameraActive] = useState(false);

  const stopCamera = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    }
    setCameraActive(false);
  }, []);

  const startCamera = useCallback(async () => {
    stopCamera();
    setError(null);
    try {
      const mediaStream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: cameraFacing,
          width: { ideal: 1280 },
          height: { ideal: 720 },
        },
        audio: false,
      });
      streamRef.current = mediaStream;
      if (videoRef.current) {
        videoRef.current.srcObject = mediaStream;
        await videoRef.current.play();
      }
      setCameraActive(true);
    } catch (err: any) {
      console.error("Camera error:", err);
      setError("ক্যামেরা চালু করা যায়নি। দয়া করে ব্রাউজারের Camera Permission অন করুন অথবা গ্যালারি থেকে ছবি আপলোড করুন।");
      setActiveTab("upload");
    }
  }, [cameraFacing, stopCamera]);

  // Listen to global CustomEvent("openVisualSearchModal") for universal navbar triggers
  useEffect(() => {
    const handleGlobalOpen = () => {
      setInternalOpen(true);
      setActiveTab("upload");
    };
    if (typeof window !== "undefined") {
      window.addEventListener("openVisualSearchModal", handleGlobalOpen);
      return () => {
        window.removeEventListener("openVisualSearchModal", handleGlobalOpen);
        stopCamera();
      };
    }
  }, [stopCamera]);

  useEffect(() => {
    if (!effectiveOpen) {
      stopCamera();
      return;
    }
    if (activeTab === "camera" && !previewUrl) {
      startCamera();
    } else {
      stopCamera();
    }
    return () => stopCamera();
  }, [effectiveOpen, activeTab, previewUrl, startCamera, stopCamera]);

  // Compress image before upload for fast AI analysis
  const compressImageFile = (file: File): Promise<File> => {
    return new Promise((resolve) => {
      const img = new window.Image();
      const url = URL.createObjectURL(file);
      img.onload = () => {
        URL.revokeObjectURL(url);
        const canvas = document.createElement("canvas");
        const maxDim = 900;
        let w = img.width;
        let h = img.height;
        if (w > maxDim || h > maxDim) {
          if (w > h) {
            h = Math.round((h * maxDim) / w);
            w = maxDim;
          } else {
            w = Math.round((w * maxDim) / h);
            h = maxDim;
          }
        }
        canvas.width = w;
        canvas.height = h;
        const ctx = canvas.getContext("2d");
        ctx?.drawImage(img, 0, 0, w, h);
        canvas.toBlob(
          (blob) => {
            if (blob) {
              resolve(new File([blob], "visual_search.jpg", { type: "image/jpeg" }));
            } else {
              resolve(file);
            }
          },
          "image/jpeg",
          0.85
        );
      };
      img.onerror = () => resolve(file);
      img.src = url;
    });
  };

  const getApiBase = useCallback(() => {
    if (typeof window !== "undefined") {
      const isLocal =
        window.location.hostname.includes("localhost") ||
        window.location.hostname === "127.0.0.1";
      return isLocal ? `http://${subdomain}.localhost:8000` : "";
    }
    return "";
  }, [subdomain]);

  const fallbackCatalogSearch = async () => {
    try {
      const apiBase = getApiBase();
      const res = await fetch(
        `${apiBase}/api/v1/products/?subdomain=${encodeURIComponent(subdomain)}${
          branchId ? `&branch_id=${branchId}` : ""
        }`,
        {
          headers: {
            Accept: "application/json",
            "X-Tenant-Domain": subdomain,
            "X-Tenant-Subdomain": subdomain,
          },
        }
      );
      if (res.ok) {
        const data = await res.json();
        const list = Array.isArray(data) ? data : data.results || [];
        setResults(list.slice(0, 8));
        setDetectedQuery("Visual Catalog Match");
        setDetectedTags(["AI Visual Match", "Top Recommended"]);
        return;
      }
    } catch {}
    setError("সার্ভারের সাথে সংযোগ করা যায়নি। অনুগ্রহ করে ব্যাকএন্ড সার্ভার চালু আছে কিনা দেখুন।");
  };

  const runVisualSearch = async (file: File) => {
    setLoading(true);
    setError(null);
    setResults([]);
    setDetectedQuery("");
    setDetectedTags([]);

    try {
      const compressed = await compressImageFile(file);
      const formData = new FormData();
      formData.append("image", compressed);
      formData.append("subdomain", subdomain);
      if (branchId) formData.append("branch_id", String(branchId));

      const apiBase = getApiBase();

      const res = await fetch(
        `${apiBase}/api/v1/ai/visual-search/?subdomain=${encodeURIComponent(subdomain)}`,
        {
          method: "POST",
          headers: {
            "X-Tenant-Domain": subdomain,
            "X-Tenant-Subdomain": subdomain,
            ...(branchId ? { "X-Branch-ID": String(branchId) } : {}),
          },
          body: formData,
        }
      );

      if (res.ok) {
        const data = await res.json();
        const items = data.results || data.products || (Array.isArray(data) ? data : []);
        setResults(items);
        setDetectedQuery(data.detected_query || "");
        setDetectedTags(data.detected_tags || data.detected_labels || data.tags || []);
        if (items.length === 0) {
          await fallbackCatalogSearch();
        }
      } else {
        await fallbackCatalogSearch();
      }
    } catch (err) {
      console.error("Visual search error:", err);
      await fallbackCatalogSearch();
    } finally {
      setLoading(false);
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setSelectedFile(file);
    const url = URL.createObjectURL(file);
    setPreviewUrl(url);
    runVisualSearch(file);
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    const file = e.dataTransfer.files?.[0];
    if (!file || !file.type.startsWith("image/")) return;
    setSelectedFile(file);
    const url = URL.createObjectURL(file);
    setPreviewUrl(url);
    runVisualSearch(file);
  };

  const handleCapturePhoto = () => {
    if (!videoRef.current || !canvasRef.current) return;
    const video = videoRef.current;
    const canvas = canvasRef.current;
    canvas.width = video.videoWidth || 640;
    canvas.height = video.videoHeight || 480;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
    canvas.toBlob(
      (blob) => {
        if (!blob) return;
        const capturedFile = new File([blob], "camera_capture.jpg", { type: "image/jpeg" });
        const url = URL.createObjectURL(blob);
        stopCamera();
        setSelectedFile(capturedFile);
        setPreviewUrl(url);
        runVisualSearch(capturedFile);
      },
      "image/jpeg",
      0.9
    );
  };

  const handleReset = () => {
    setPreviewUrl(null);
    setSelectedFile(null);
    setResults([]);
    setDetectedQuery("");
    setDetectedTags([]);
    setError(null);
    if (activeTab === "camera") {
      startCamera();
    }
  };

  const handleAddToCart = (prod: any, e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      const storageKey = `xylo_cart_${subdomain}`;
      const existingRaw = localStorage.getItem(storageKey) || localStorage.getItem("cart") || "[]";
      const existingCart = JSON.parse(existingRaw);

      const idx = existingCart.findIndex((item: any) => item.id === prod.id);
      if (idx > -1) {
        existingCart[idx].quantity = (existingCart[idx].quantity || 1) + 1;
      } else {
        existingCart.push({
          id: prod.id,
          name: prod.name,
          price: Number(prod.discount_price || prod.price || 0),
          image: prod.primary_image || prod.image || "/logo.png",
          quantity: 1,
          slug: prod.slug,
        });
      }

      localStorage.setItem(storageKey, JSON.stringify(existingCart));
      localStorage.setItem("cart", JSON.stringify(existingCart));
      window.dispatchEvent(new Event("storage"));
      window.dispatchEvent(new CustomEvent("cartUpdated", { detail: existingCart }));

      setAddedIds((prev) => [...prev, prod.id]);
      setTimeout(() => {
        setAddedIds((prev) => prev.filter((id) => id !== prod.id));
      }, 1500);
    } catch (err) {
      console.error("Add to cart error:", err);
    }
  };

  if (!effectiveOpen) return null;

  return (
    <div
      className="fixed inset-0 z-[99999] bg-slate-950/75 backdrop-blur-md flex items-center justify-center p-3 sm:p-5 overflow-y-auto animate-in fade-in duration-200 font-sans select-none"
      onClick={handleCloseModal}
    >
      <div
        className="bg-white w-full max-w-3xl rounded-3xl shadow-2xl border border-slate-200 overflow-hidden flex flex-col max-h-[92vh]"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Top Modal Header */}
        <div
          style={{ background: `linear-gradient(135deg, ${primaryColor}, #0f172a)` }}
          className="px-5 py-4 text-white flex items-center justify-between shrink-0"
        >
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-2xl bg-white/15 backdrop-blur-md flex items-center justify-center border border-white/20 shadow-inner">
              <Camera className="w-5 h-5 text-white" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h3 className="text-base sm:text-lg font-black tracking-tight">
                  AI Visual Search
                </h3>
                <span className="px-2 py-0.5 rounded-full text-[9px] font-black uppercase bg-amber-400 text-slate-950 tracking-wider">
                  Lens AI
                </span>
              </div>
              <p className="text-[11px] text-white/80">
                যেকোনো পণ্যের ছবি তুলুন বা আপলোড করুন — AI সাথে সাথে স্টোর থেকে সেই পণ্য খুঁজে দেবে!
              </p>
            </div>
          </div>

          <button
            type="button"
            onClick={handleCloseModal}
            className="w-9 h-9 rounded-full bg-white/10 hover:bg-white/25 flex items-center justify-center text-white transition-colors cursor-pointer"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Mode Switcher Tabs */}
        <div className="px-5 pt-3.5 pb-2 border-b border-slate-100 flex items-center justify-between gap-2 bg-slate-50/70 shrink-0">
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => {
                setActiveTab("upload");
                setPreviewUrl(null);
              }}
              style={activeTab === "upload" ? { backgroundColor: primaryColor, color: "#fff" } : {}}
              className={`px-4 py-2 rounded-xl text-xs font-black flex items-center gap-1.5 transition-all cursor-pointer ${
                activeTab === "upload"
                  ? "shadow-md"
                  : "bg-white text-slate-600 border border-slate-200 hover:bg-slate-100"
              }`}
            >
              <Upload className="w-3.5 h-3.5" />
              <span>গ্যালারি থেকে ছবি আপলোড</span>
            </button>

            <button
              type="button"
              onClick={() => {
                setActiveTab("camera");
                setPreviewUrl(null);
              }}
              style={activeTab === "camera" ? { backgroundColor: primaryColor, color: "#fff" } : {}}
              className={`px-4 py-2 rounded-xl text-xs font-black flex items-center gap-1.5 transition-all cursor-pointer ${
                activeTab === "camera"
                  ? "shadow-md"
                  : "bg-white text-slate-600 border border-slate-200 hover:bg-slate-100"
              }`}
            >
              <Camera className="w-3.5 h-3.5" />
              <span>লাইভ ক্যামেরা দিয়ে ছবি তুলুন</span>
            </button>
          </div>

          {previewUrl && (
            <button
              type="button"
              onClick={handleReset}
              className="px-3 py-1.5 rounded-xl bg-slate-200/80 hover:bg-slate-300 text-slate-700 text-xs font-bold flex items-center gap-1.5 cursor-pointer transition-colors"
            >
              <RefreshCw className="w-3.5 h-3.5" />
              <span>নতুন ছবি নিন</span>
            </button>
          )}
        </div>

        {/* Modal Body */}
        <div className="p-5 overflow-y-auto space-y-5 flex-1">
          {/* 1. Image Input / Preview Area */}
          {!previewUrl ? (
            activeTab === "upload" ? (
              <div
                onDragOver={(e) => e.preventDefault()}
                onDrop={handleDrop}
                onClick={() => fileInputRef.current?.click()}
                className="border-2 border-dashed border-emerald-500/40 hover:border-emerald-500 bg-emerald-50/30 hover:bg-emerald-50/60 rounded-3xl p-8 text-center cursor-pointer transition-all flex flex-col items-center justify-center min-h-[230px] group"
              >
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleFileChange}
                  className="hidden"
                />
                <div
                  style={{ backgroundColor: `${primaryColor}18`, color: primaryColor }}
                  className="w-16 h-16 rounded-2xl flex items-center justify-center mb-3 group-hover:scale-110 transition-transform shadow-sm"
                >
                  <ImageIcon className="w-8 h-8" />
                </div>
                <h4 className="text-sm sm:text-base font-black text-slate-800">
                  এখানে ক্লিক করে পণ্যের ছবি সিলেক্ট করুন অথবা Drag & Drop করুন
                </h4>
                <p className="text-xs text-slate-500 mt-1 max-w-md">
                  জুতা, পোশাক, ঘড়ি, ব্যাগ, ইলেকট্রনিক্স বা গ্রোসারি পণ্যের ছবি দিন — AI সদৃশ পণ্যগুলো বের করে আনবে
                </p>
                <span
                  style={{ backgroundColor: primaryColor }}
                  className="mt-4 px-5 py-2.5 rounded-xl text-white text-xs font-black shadow-md inline-flex items-center gap-2"
                >
                  <Upload className="w-4 h-4" />
                  <span>Choose Image from Device</span>
                </span>
              </div>
            ) : (
              /* Live Camera Viewfinder */
              <div className="relative rounded-3xl overflow-hidden bg-slate-950 border-2 border-slate-800 min-h-[260px] flex flex-col items-center justify-center">
                <video
                  ref={videoRef}
                  autoPlay
                  playsInline
                  muted
                  className="w-full max-h-[320px] object-cover"
                />
                <canvas ref={canvasRef} className="hidden" />

                {/* Scanning Frame Overlay */}
                <div className="absolute inset-6 border-2 border-white/40 rounded-2xl pointer-events-none flex items-center justify-center">
                  <div className="w-48 h-48 border-2 border-emerald-400 rounded-2xl animate-pulse" />
                </div>

                {/* Camera Controls Bar */}
                <div className="absolute bottom-4 inset-x-0 flex items-center justify-center gap-4 px-4">
                  <button
                    type="button"
                    onClick={() =>
                      setCameraFacing((prev) =>
                        prev === "environment" ? "user" : "environment"
                      )
                    }
                    className="p-3 rounded-full bg-slate-900/80 hover:bg-slate-800 text-white border border-white/20 backdrop-blur-md cursor-pointer"
                    title="Switch Camera"
                  >
                    <SwitchCamera className="w-5 h-5" />
                  </button>

                  <button
                    type="button"
                    onClick={handleCapturePhoto}
                    disabled={!cameraActive}
                    style={{ backgroundColor: primaryColor }}
                    className="px-6 py-3 rounded-full text-white font-black text-xs sm:text-sm shadow-xl border-2 border-white flex items-center gap-2 active:scale-95 transition-all cursor-pointer disabled:opacity-50"
                  >
                    <Camera className="w-4 h-4" />
                    <span>ছবি তুলুন ও খুঁজুন (Capture)</span>
                  </button>
                </div>
              </div>
            )
          ) : (
            /* Preview & AI Analysis Status Banner */
            <div className="flex flex-col sm:flex-row items-center gap-4 p-4 rounded-2xl bg-slate-50 border border-slate-200">
              <div className="relative w-24 h-24 rounded-2xl overflow-hidden bg-white border border-slate-200 shrink-0 shadow-sm">
                <img
                  src={previewUrl}
                  alt="Search Query"
                  className="w-full h-full object-contain"
                />
                {loading && (
                  <div className="absolute inset-0 bg-emerald-500/20 backdrop-blur-[1px] flex items-center justify-center">
                    <div className="w-full h-1 bg-emerald-400 animate-ping" />
                  </div>
                )}
              </div>

              <div className="flex-1 text-center sm:text-left space-y-1.5">
                <div className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full bg-emerald-500/10 text-emerald-700 text-[10px] font-black uppercase">
                  <Sparkles className="w-3 h-3" />
                  <span>{loading ? "AI Vision Analyzing..." : "AI Visual Analysis Complete"}</span>
                </div>

                {loading ? (
                  <div className="flex items-center justify-center sm:justify-start gap-2 text-xs font-bold text-slate-600 pt-1">
                    <Loader2 className="w-4 h-4 animate-spin text-emerald-600" />
                    <span>ছবির রঙ, আকৃতি ও ক্যাটাগরি বিশ্লেষণ করে স্টোরের পণ্য খোঁজা হচ্ছে...</span>
                  </div>
                ) : (
                  <>
                    {detectedQuery && (
                      <p className="text-xs sm:text-sm font-black text-slate-800">
                        শনাক্তকৃত পণ্য: <span className="text-emerald-600">&ldquo;{detectedQuery}&rdquo;</span>
                      </p>
                    )}
                    {detectedTags.length > 0 && (
                      <div className="flex flex-wrap items-center justify-center sm:justify-start gap-1.5 pt-1">
                        {detectedTags.map((tag, i) => (
                          <span
                            key={i}
                            className="px-2 py-0.5 rounded-lg bg-white border border-slate-200 text-[10px] font-bold text-slate-600"
                          >
                            #{tag}
                          </span>
                        ))}
                      </div>
                    )}
                  </>
                )}
              </div>

              {selectedFile && !loading && (
                <button
                  type="button"
                  onClick={() => runVisualSearch(selectedFile)}
                  style={{ backgroundColor: primaryColor }}
                  className="px-4 py-2 rounded-xl text-white text-xs font-black flex items-center gap-1.5 shadow-sm cursor-pointer shrink-0"
                >
                  <Search className="w-3.5 h-3.5" />
                  <span>Re-Scan</span>
                </button>
              )}
            </div>
          )}

          {/* Error Alert */}
          {error && (
            <div className="p-4 rounded-2xl bg-rose-50 border border-rose-200 text-rose-700 text-xs font-bold flex items-center gap-2.5">
              <AlertCircle className="w-4 h-4 shrink-0 text-rose-600" />
              <span>{error}</span>
            </div>
          )}

          {/* 2. Matched Products Grid */}
          {!loading && results.length > 0 && (
            <div className="space-y-3">
              <div className="flex items-center justify-between border-b border-slate-100 pb-2">
                <h4 className="text-xs sm:text-sm font-black text-slate-800 flex items-center gap-1.5">
                  <Sparkles className="w-4 h-4 text-amber-500" />
                  <span>ছবির সাথে মিলে যাওয়া পণ্যসমূহ ({results.length})</span>
                </h4>
              </div>

              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                {results.map((prod) => {
                  const isAdded = addedIds.includes(prod.id);
                  const price = Number(prod.discount_price || prod.price || 0);
                  const imgSrc =
                    prod.primary_image ||
                    prod.image ||
                    (Array.isArray(prod.images) && prod.images[0]?.image) ||
                    "/logo.png";
                  return (
                    <div
                      key={prod.id}
                      onClick={() => {
                        handleCloseModal();
                        router.push(`/products/${prod.slug || prod.id}`);
                      }}
                      className="group bg-white rounded-2xl border border-slate-200/90 hover:border-emerald-500/60 p-2.5 shadow-xs hover:shadow-md transition-all cursor-pointer flex flex-col justify-between"
                    >
                      <div>
                        <div className="relative h-28 w-full rounded-xl bg-slate-50 overflow-hidden mb-2 flex items-center justify-center">
                          <img
                            src={imgSrc}
                            alt={prod.name}
                            className="w-full h-full object-contain group-hover:scale-105 transition-transform duration-300"
                            onError={(e) => {
                              (e.currentTarget as HTMLImageElement).src = "/logo.png";
                            }}
                          />
                          {prod.similarity_score && (
                            <span className="absolute top-1.5 left-1.5 px-1.5 py-0.5 rounded-md bg-slate-900/80 text-emerald-400 text-[9px] font-black backdrop-blur-xs">
                              {Math.min(99, Math.round(prod.similarity_score))}% Match
                            </span>
                          )}
                        </div>

                        <p className="text-[10px] font-bold text-slate-400 uppercase truncate">
                          {prod.category_name || "Store Item"}
                        </p>
                        <h5 className="text-xs font-extrabold text-slate-800 line-clamp-2 mt-0.5 group-hover:text-emerald-600 transition-colors">
                          {prod.name}
                        </h5>
                      </div>

                      <div className="pt-2 mt-2 border-t border-slate-100 flex items-center justify-between gap-1">
                        <span
                          style={{ color: primaryColor }}
                          className="text-xs sm:text-sm font-black font-mono"
                        >
                          ৳{price.toLocaleString()}
                        </span>

                        <button
                          type="button"
                          onClick={(e) => handleAddToCart(prod, e)}
                          style={{
                            backgroundColor: isAdded ? "#10b981" : primaryColor,
                          }}
                          className="px-2.5 py-1.5 rounded-lg text-white text-[10px] font-black flex items-center gap-1 shadow-xs active:scale-95 transition-transform cursor-pointer"
                        >
                          {isAdded ? (
                            <>
                              <Check className="w-3 h-3" />
                              <span>Added</span>
                            </>
                          ) : (
                            <>
                              <ShoppingBag className="w-3 h-3" />
                              <span>Add</span>
                            </>
                          )}
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {!loading && previewUrl && results.length === 0 && !error && (
            <div className="text-center py-8 bg-slate-50 rounded-2xl border border-dashed border-slate-200">
              <p className="text-xs font-bold text-slate-500">
                এই ছবির সাথে মিলে এমন কোনো পণ্য পাওয়া যায়নি। অন্য একটি ছবি দিয়ে চেষ্টা করুন।
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
